import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { CustomOption, FileTypeEnum, IFile } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { DateRangeInput } from '@repo/ui/date-range-input'
import UploadFileInput from '@repo/ui/file-input'
import { DownloadFile } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Switch from '@repo/ui/switch'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import ControlledSwitch from '../../components/elements/controlled-switch'
import { Form, FormControl, FormField, FormItem } from '../../components/elements/forms/form'
import { ControlledAdressInput } from '../../components/elements/inputs/address-input'
import ControlledDateStringInput from '../../components/elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../components/elements/inputs/controlled-hour-input'
import ControlledInput from '../../components/elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../components/elements/inputs/controlled-multi-select-input'
import ControlledPhoneInput from '../../components/elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../components/elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../components/elements/inputs/controlled-select-input'
import useTranslate from '../../lib/hooks/use-translate'
import { Time } from '../../lib/types/utils'
import {
  createDateStringSchema,
  createTimeSchema
} from '../../lib/utils/validations/date.validation'
import { FILE_VALIDATION } from '../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../lib/utils/validations/phone-number.validation'
import {
  createStringArraySchema,
  createStringSchema
} from '../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../utils/constants/form.constants'

type UiInputsType = {
  text1: string
  text2: string
  text3: number
  text4: string
  text5: string
  text6: string
  text7: string
  text8: string[]
  text9: string[]
  text10: string[]
  text11: string
  text12: string
  text13: string
  time: Time
  date: string
  files: IFile[]
  radioValue: boolean
  checked: boolean
  checkbox: boolean
}

const options: CustomOption<string>[] = [
  {
    label: 'Option 1',
    value: 'option1'
  },
  {
    label: 'Option 2',
    value: 'option2'
  },
  {
    label: 'Option 3',
    value: 'option3'
  }
]

const formSchema: (t: TFunction<AvailableLanguages, undefined>) => ZodType<UiInputsType> = (t) =>
  z.object({
    text1: createStringSchema('Text 1'),
    text2: createStringSchema('Password'),
    text3: createNumberSchema('Number'),
    text4: createStringSchema('Email', { isEmail: true }),
    text5: createStringSchema('Text 1'),
    text6: createStringSchema('Select Input'),
    text7: createStringSchema('Select Input Autocomplete'),
    text8: createStringArraySchema('Multi Select Input'),
    text9: createStringArraySchema('Multi Select Input Autocomplete'),
    text10: createStringArraySchema('Multi Select Input - No Options'),
    text11: PHONE_NUMBER_SCHEMA(t),
    text12: createStringSchema('Select Input Autocomplete - New Option'),
    text13: createStringSchema('Address Input'),
    time: createTimeSchema({ inputName: 'Time input' }),
    date: createDateStringSchema({ inputName: 'Date' }),
    files: z.array(FILE_VALIDATION()),
    radioValue: z.boolean(),
    checked: z.boolean(),
    checkbox: z.boolean()
  })

const UiInputsPage = () => {
  const { t } = useTranslate()
  const form = useForm<UiInputsType>({
    resolver: zodResolver(formSchema(t)),
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((data) => console.log(data))}>
        <div className="bg-white  flex flex-col gap-5 p-5">
          <FormField
            name="text1"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput required {...field} type="text" label="Text Input" />
                </FormControl>
              </FormItem>
            )}
          />
          <Input disabled type="text" value={'John'} label="Disabled Input" />
          <Input readOnly type="text" value={'John'} label="Readonly Input" />

          <FormField
            control={form.control}
            name="text2"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    wrapperClassName="bg-white"
                    {...field}
                    type="password"
                    label={'Password'}
                    placeholder={'Password'}
                    autoComplete={'new-password'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="text3"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput required {...field} type="number" label="Number Input" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text4"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput {...field} label="Email" type="email" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name="time"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledHourInput {...field} required label="Time input" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text6"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    options={options}
                    label={'Select Input'}
                    selectedOption={options.find((option) => option.value === field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text7"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    searchable
                    options={options}
                    label={'Select Input Autocomplete'}
                    selectedOption={options.find((option) => option.value === field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text12"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    searchable
                    allowNewOption
                    options={options}
                    label={'Select Input Autocomplete - New Option'}
                    selectedOption={
                      field.value
                        ? options.find((option) => option.value === field.value)
                        : undefined
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'text8'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label={'Multi Select Input'}
                    variant={'primary'}
                    options={options}
                    selectedOptions={
                      options.filter((option) => field.value?.includes(option.value)) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'text9'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    searchable
                    label={'Multi Select Input Autocomplete'}
                    variant={'primary'}
                    options={options}
                    selectedOptions={
                      options.filter((option) => field.value?.includes(option.value)) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'text10'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    required
                    {...field}
                    label="Multi Select Input - No Options"
                    variant="blue"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text11"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledPhoneInput {...field} required defaultCountry="FR" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="text13"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledAdressInput {...field} required label="Address Input" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput {...field} required label="Date" />
                </FormControl>
              </FormItem>
            )}
          />

          <DateRangeInput label="Date range" />

          <FormField
            control={form.control}
            name={'files'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <UploadFileInput
                    maxSize={5 * 1024 * 1024}
                    allowedExtensions={['png', 'jpg', 'jpeg']}
                    allowedAspectRatio="1:1"
                    placeholder="Glisser-déposer ou parcourir"
                    types={[FileTypeEnum.IMAGE]}
                    icon={DownloadFile}
                    label={'Déposer des images'}
                    multiple
                    files={field?.value}
                    onFilesChange={(files) => field.onChange(files)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="radioValue"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledRadioInput
                    {...field}
                    label={'Radio Input'}
                    checked={field.value}
                    onCheck={() => field.onChange(!field.value)}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'checked'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSwitch
                    {...field}
                    label={'Switch Input'}
                    variant={'tag'}
                    withBorder
                    borderClassName={'border-grey-light px-3 py-2 rounded'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Switch
            label={'Disabled Switch Input'}
            labelVariant="tag"
            disabled
            withBorder
            borderClassName={'border-grey-light px-3 py-2 rounded'}
          />

          <Checkbox
            label="Click me"
            checked={form.watch('checkbox')}
            onChange={(checked) => form.setValue('checkbox', checked)}
          />
          <div className="flex justify-end ">
            <Button type="submit">Submit</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default UiInputsPage
